import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Trash2, Phone, Inbox } from "lucide-react";
import { categoryById } from "@/data/categories";
import { API } from "@/lib/api";

const STATUS_LABELS = { new: "ახალი", contacted: "დავუკავშირდი", done: "დასრულებული" };
const STATUS_STYLES = {
  new: "bg-green-500/15 text-green-400 border-green-500/40",
  contacted: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  done: "bg-white/10 text-gray-300 border-white/25",
};

const selectCls =
  "rounded-lg bg-white/5 border border-white/15 px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#D4AF37]/60";

export default function AdminApplications({ auth }) {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = () => {
    axios.get(`${API}/admin/applications`, auth).then((r) => setItems(r.data)).catch(() => setItems([]));
  };

  useEffect(load, []);

  const setStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/applications/${id}/status`, { status }, auth);
      setItems((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success("სტატუსი შეიცვალა");
    } catch {
      toast.error("შეცვლა ვერ მოხერხდა");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("ნამდვილად გსურთ განაცხადის წაშლა?")) return;
    try {
      await axios.delete(`${API}/admin/applications/${id}`, auth);
      setItems((prev) => prev.filter((a) => a.id !== id));
      toast.success("განაცხადი წაიშალა");
    } catch {
      toast.error("წაშლა ვერ მოხერხდა");
    }
  };

  if (items === null) return <p className="text-gray-500">იტვირთება...</p>;

  const newCount = items.filter((a) => a.status === "new").length;

  return (
    <div data-testid="admin-applications-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">
          განაცხადები ({items.length})
          {newCount > 0 && <span className="ml-3 text-sm text-green-400 font-normal" data-testid="applications-new-count">{newCount} ახალი</span>}
        </h2>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-white/20 rounded-xl p-12 text-center text-gray-500 flex flex-col items-center gap-3" data-testid="applications-empty">
          <Inbox className="w-8 h-8" />
          განაცხადები ჯერ არ არის
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((a) => {
            const cat = categoryById(a.vehicle);
            const open = openId === a.id;
            return (
              <div key={a.id} className="card-lux p-5" data-testid={`application-row-${a.id}`}>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3 justify-between">
                  <div className="min-w-0">
                    <p className="text-white font-semibold" data-testid={`application-name-${a.id}`}>{a.name}</p>
                    <a href={`tel:${a.phone}`} className="text-sm text-gray-400 flex items-center gap-1.5 mt-1 hover:text-white transition-colors">
                      <Phone className="w-3.5 h-3.5 text-[#D4AF37]" /> {a.phone}
                    </a>
                  </div>
                  <div className="text-sm text-gray-400">
                    {a.vehicle_label || (cat ? `${cat.name} ${cat.years}` : a.vehicle) || "კონსულტაცია"}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(a.created_at).toLocaleString("ka-GE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={a.status}
                      onChange={(e) => setStatus(a.id, e.target.value)}
                      data-testid={`application-status-${a.id}`}
                      className={`${selectCls} ${STATUS_STYLES[a.status] || ""}`}
                    >
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k} className="bg-[#141721] text-white">{v}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setOpenId(open ? null : a.id)}
                      data-testid={`application-expand-${a.id}`}
                      className="w-8 h-8 rounded-lg bg-white/5 border border-white/15 text-gray-300 flex items-center justify-center hover:border-white/40"
                    >
                      {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => remove(a.id)}
                      data-testid={`application-delete-${a.id}`}
                      className="w-8 h-8 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 flex items-center justify-center hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {open && (
                  <div className="mt-4 pt-4 border-t border-white/10 text-sm text-gray-300 space-y-2" data-testid={`application-details-${a.id}`}>
                    <p><span className="text-gray-500">სახელი:</span> {a.name}</p>
                    <p><span className="text-gray-500">ტელეფონი:</span> {a.phone}</p>
                    <p><span className="text-gray-500">სასურველი ავტომობილი:</span> {a.vehicle_label || (cat ? `${cat.name} ${cat.years}` : a.vehicle) || "ჯერ არ უცნობია"}</p>
                    <p><span className="text-gray-500">კომენტარი:</span> {a.comment || "—"}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
